// User-friendly error UI components

import {
	type ErrorRecoveryStrategy,
	ErrorSeverity,
	type MonologError,
} from "./error-handler.js";

class ErrorUI {
	private static instance: ErrorUI;
	private errorContainer: HTMLElement | null = null;
	private currentNotification: HTMLElement | null = null;

	private constructor() {}

	static getInstance(): ErrorUI {
		if (!ErrorUI.instance) {
			ErrorUI.instance = new ErrorUI();
		}
		return ErrorUI.instance;
	}

	private ensureErrorContainer(): boolean {
		if (typeof document === "undefined") return false;
		if (this.errorContainer) return true;

		// Create error notification container
		this.errorContainer = document.createElement("div");
		this.errorContainer.id = "error-notifications";
		this.errorContainer.className =
			"fixed top-5 right-5 z-[10000] max-w-[400px] pointer-events-none flex flex-col gap-2.5";
		document.body.appendChild(this.errorContainer);
		return true;
	}

	// Show error notification to user
	showError(error: MonologError, recovery: ErrorRecoveryStrategy): void {
		if (!this.ensureErrorContainer()) return;

		// Remove existing notification
		this.clearCurrentNotification();

		// Create notification element
		const notification = this.createNotificationElement(error, recovery);
		this.currentNotification = notification;
		this.errorContainer?.appendChild(notification);

		// Auto-hide for low severity errors
		if (error.severity === ErrorSeverity.LOW) {
			setTimeout(() => {
				this.hideNotification(notification);
			}, 5000);
		} else if (error.severity === ErrorSeverity.MEDIUM) {
			setTimeout(() => {
				this.hideNotification(notification);
			}, 10000);
		}
		// High and critical errors stay until manually dismissed
	}

	private createNotificationElement(
		error: MonologError,
		recovery: ErrorRecoveryStrategy,
	): HTMLElement {
		const notification = document.createElement("div");
		notification.className = `p-4 rounded-lg shadow-lg pointer-events-auto animate-slide-in text-white text-sm leading-relaxed ${this.getSeverityClass(error.severity)}`;

		// Create notification content
		const content = document.createElement("div");

		// Title
		const title = document.createElement("div");
		title.className = "font-bold mb-1";
		title.textContent = this.getSeverityTitle(error.severity);
		content.appendChild(title);

		// Message
		const message = document.createElement("div");
		message.className = "mb-3 opacity-90";
		message.textContent = recovery.userMessage;
		content.appendChild(message);

		// Actions
		const actions = document.createElement("div");
		actions.className = "flex gap-2 justify-end";

		const buttonBaseClass =
			"bg-white/20 border border-white/30 text-white px-3 py-1.5 rounded cursor-pointer text-xs hover:bg-white/30 transition focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:outline-none active:scale-95";

		// Retry button (if retryable)
		if (recovery.retryable) {
			const retryButton = document.createElement("button");
			retryButton.textContent = recovery.actionLabel || "再試行";
			retryButton.className = buttonBaseClass;
			retryButton.onclick = () => {
				this.hideNotification(notification);
				// Emit retry event
				window.dispatchEvent(
					new CustomEvent("error-retry", { detail: { error, recovery } }),
				);
			};
			actions.appendChild(retryButton);
		}

		// Dismiss button
		const dismissButton = document.createElement("button");
		dismissButton.textContent = "閉じる";
		dismissButton.className = buttonBaseClass;
		dismissButton.onclick = () => {
			this.hideNotification(notification);
		};
		actions.appendChild(dismissButton);

		content.appendChild(actions);
		notification.appendChild(content);

		return notification;
	}

	private getSeverityClass(severity: ErrorSeverity): string {
		switch (severity) {
			case ErrorSeverity.LOW:
				return "bg-blue-600";
			case ErrorSeverity.MEDIUM:
				return "bg-orange-600";
			case ErrorSeverity.HIGH:
				return "bg-red-600";
			case ErrorSeverity.CRITICAL:
				return "bg-purple-700";
			default:
				return "bg-zinc-600";
		}
	}

	private getSeverityTitle(severity: ErrorSeverity): string {
		switch (severity) {
			case ErrorSeverity.LOW:
				return "情報";
			case ErrorSeverity.MEDIUM:
				return "警告";
			case ErrorSeverity.HIGH:
				return "エラー";
			case ErrorSeverity.CRITICAL:
				return "致命的なエラー";
			default:
				return "通知";
		}
	}

	private hideNotification(notification: HTMLElement): void {
		notification.className = notification.className.replace(
			"animate-slide-in",
			"animate-slide-out",
		);
		setTimeout(() => {
			if (notification.parentNode) {
				notification.parentNode.removeChild(notification);
			}
			if (this.currentNotification === notification) {
				this.currentNotification = null;
			}
		}, 300);
	}

	private clearCurrentNotification(): void {
		if (this.currentNotification) {
			this.hideNotification(this.currentNotification);
		}
	}

	// Show loading state
	showLoading(message: string): HTMLElement {
		const loading = document.createElement("div");
		loading.className =
			"fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[10001] text-white";

		const content = document.createElement("div");
		content.className = "text-center";

		const spinner = document.createElement("div");
		spinner.className =
			"w-10 h-10 border-4 border-white/30 border-t-white rounded-full animate-spin-slow mx-auto mb-4";

		const text = document.createElement("div");
		text.textContent = message;
		text.className = "text-base font-medium";

		content.appendChild(spinner);
		content.appendChild(text);
		loading.appendChild(content);
		document.body.appendChild(loading);

		return loading;
	}

	// Hide loading state
	hideLoading(loadingElement: HTMLElement): void {
		if (loadingElement.parentNode) {
			loadingElement.parentNode.removeChild(loadingElement);
		}
	}

	/**
	 * Show a simple toast notification
	 */
	showNotification(
		message: string,
		severity: ErrorSeverity = ErrorSeverity.LOW,
		duration = 3000,
	): void {
		if (!this.ensureErrorContainer()) return;

		const notification = document.createElement("div");
		notification.className = `p-3.5 px-4 rounded-lg shadow-lg pointer-events-auto animate-slide-in text-white text-sm leading-relaxed cursor-pointer ${this.getSeverityClass(severity)}`;

		notification.textContent = message;
		notification.onclick = () => this.hideNotification(notification);

		this.errorContainer?.appendChild(notification);

		if (duration > 0) {
			setTimeout(() => {
				this.hideNotification(notification);
			}, duration);
		}
	}

	// Show confirmation dialog
	showConfirmation(
		message: string,
		onConfirm: () => void,
		onCancel?: () => void,
		confirmText = "OK",
		cancelText = "キャンセル",
	): void {
		const overlay = document.createElement("div");
		overlay.className =
			"fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[10002] p-4";

		const dialog = document.createElement("div");
		dialog.className =
			"bg-card-bg border border-border-main p-6 rounded-xl max-w-[400px] w-full shadow-2xl animate-in fade-in zoom-in duration-200";

		const messageEl = document.createElement("div");
		messageEl.textContent = message;
		messageEl.className =
			"text-text-main opacity-90 mb-6 text-base leading-relaxed";

		const actions = document.createElement("div");
		actions.className = "flex gap-3 justify-end";

		const cancelButton = document.createElement("button");
		cancelButton.textContent = cancelText;
		cancelButton.className =
			"bg-white/5 hover:bg-white/10 border border-white/10 text-text-dim px-4 py-2 rounded-lg cursor-pointer text-sm transition focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none active:scale-95";
		cancelButton.onclick = () => {
			document.body.removeChild(overlay);
			if (onCancel) onCancel();
		};
		actions.appendChild(cancelButton);

		const confirmButton = document.createElement("button");
		confirmButton.textContent = confirmText;
		confirmButton.className =
			"bg-primary hover:bg-primary-hover text-white px-5 py-2 rounded-lg cursor-pointer text-sm transition font-medium focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg-main focus-visible:outline-none active:scale-95";
		confirmButton.onclick = () => {
			document.body.removeChild(overlay);
			onConfirm();
		};
		actions.appendChild(confirmButton);

		dialog.appendChild(messageEl);
		dialog.appendChild(actions);
		overlay.appendChild(dialog);
		document.body.appendChild(overlay);
	}

	/**
	 * Show a prompt dialog
	 */
	showPrompt(
		message: string,
		defaultValue: string,
		onConfirm: (value: string) => void,
		onCancel?: () => void,
		confirmText = "OK",
		cancelText = "キャンセル",
	): void {
		const overlay = document.createElement("div");
		overlay.className =
			"fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[10002] p-4";

		const dialog = document.createElement("div");
		dialog.className =
			"bg-card-bg border border-border-main p-6 rounded-xl max-w-[400px] w-full shadow-2xl animate-in fade-in zoom-in duration-200";

		const messageEl = document.createElement("div");
		messageEl.textContent = message;
		messageEl.className =
			"text-text-main opacity-90 mb-4 text-base leading-relaxed";

		const input = document.createElement("input");
		input.type = "text";
		input.value = defaultValue;
		input.className =
			"w-full px-3 py-2.5 bg-bg-main border border-border-main rounded-lg mb-6 text-text-main text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus-visible:ring-2 focus-visible:ring-primary transition-all";

		const actions = document.createElement("div");
		actions.className = "flex gap-3 justify-end";

		const cancelButton = document.createElement("button");
		cancelButton.textContent = cancelText;
		cancelButton.className =
			"bg-white/5 hover:bg-white/10 border border-white/10 text-text-dim px-4 py-2 rounded-lg cursor-pointer text-sm transition focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none active:scale-95";
		cancelButton.onclick = () => {
			document.body.removeChild(overlay);
			if (onCancel) onCancel();
		};

		const confirmButton = document.createElement("button");
		confirmButton.textContent = confirmText;
		confirmButton.className =
			"bg-primary hover:bg-primary-hover text-white px-5 py-2 rounded-lg cursor-pointer text-sm transition font-medium focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg-main focus-visible:outline-none active:scale-95";
		confirmButton.onclick = () => {
			const value = input.value;
			document.body.removeChild(overlay);
			onConfirm(value);
		};

		actions.appendChild(cancelButton);
		actions.appendChild(confirmButton);

		dialog.appendChild(messageEl);
		dialog.appendChild(input);
		dialog.appendChild(actions);
		overlay.appendChild(dialog);
		document.body.appendChild(overlay);

		// Focus input and select text
		setTimeout(() => {
			input.focus();
			input.select();
		}, 0);

		// Handle Enter key
		input.onkeydown = (e) => {
			if (e.key === "Enter") {
				confirmButton.click();
			} else if (e.key === "Escape") {
				cancelButton.click();
			}
		};
	}
}

// Global error UI instance
export const errorUI = ErrorUI.getInstance();
